'use client';

import { API_BASE_URL } from '@/slw/shared/consts/common.const';
import { getTokenClient, saveTokenClient } from './auth.client';

export interface RefreshTokenResponse {
  success: boolean;
  data?: {
    accessToken: string;
    refreshToken: string;
    expiresAt: string;
  };
  message?: string;
}

export interface TokenInfo {
  accessToken: string;
  refreshToken: string;
  expiresAt: Date;
}

class RefreshTokenService {
  private static instance: RefreshTokenService;
  private refreshPromise: Promise<RefreshTokenResponse> | null = null;

  private constructor() {}

  public static getInstance(): RefreshTokenService {
    if (!RefreshTokenService.instance) {
      RefreshTokenService.instance = new RefreshTokenService();
    }
    return RefreshTokenService.instance;
  }

  /**
   * Get refresh token from localStorage
   */
  private getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('refresh_token');
  }

  /**
   * Get access token from httpOnly cookie
   */
  private getAccessToken(): string | null {
    if (typeof window === 'undefined') return null;
    return getTokenClient();
  }

  /**
   * Save tokens to httpOnly cookies and localStorage
   */
  private saveTokens(tokens: TokenInfo): void {
    if (typeof window === 'undefined') return;
    
    // Save access token to httpOnly cookie
    saveTokenClient(tokens.accessToken);
    
    // Save refresh token to localStorage for client-side logic
    localStorage.setItem('refresh_token', tokens.refreshToken);
    localStorage.setItem('token_expires_at', tokens.expiresAt.toISOString());
  }

  /**
   * Clear all tokens
   */
  private clearTokens(): void {
    if (typeof window === 'undefined') return;
    
    // Clear httpOnly cookie (will be handled by server)
    document.cookie = 'auth_client=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    
    // Clear localStorage
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
  }

  /**
   * Check if access token is expired or will expire soon (within 5 minutes)
   */
  public isTokenExpiredOrExpiringSoon(): boolean {
    // In development, tạm thời tắt auto refresh để tránh báo "Session expired"
    // liên tục khi đang debug hoặc BE refresh token chưa ổn định.
    if (process.env.NODE_ENV === 'development') {
      return false;
    }

    const expiresAt = localStorage.getItem('token_expires_at');
    // Nếu chưa có thông tin hết hạn, coi như CHƯA hết hạn
    // để tránh gọi /auth/refresh ngay sau khi login.
    if (!expiresAt) return false;

    const expiryTime = new Date(expiresAt).getTime();
    const currentTime = new Date().getTime();
    const fiveMinutesInMs = 5 * 60 * 1000; // 5 minutes

    return currentTime >= (expiryTime - fiveMinutesInMs);
  }

  /**
   * Refresh access token using refresh token
   */
  public async refreshAccessToken(): Promise<RefreshTokenResponse> {
    // If there's already a refresh in progress, return the same promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return {
        success: false,
        message: 'No refresh token available'
      };
    }

    this.refreshPromise = this.performRefresh(refreshToken);
    
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performRefresh(refreshToken: string): Promise<RefreshTokenResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      const result: RefreshTokenResponse = await response.json();

      if (result.success && result.data) {
        // Save new tokens
        this.saveTokens({
          accessToken: result.data.accessToken,
          refreshToken: result.data.refreshToken,
          expiresAt: new Date(result.data.expiresAt)
        });
      } else {
        // If refresh fails, clear all tokens
        this.clearTokens();
      }

      return result;
    } catch (error) {
      this.clearTokens();
      return {
        success: false,
        message: 'Failed to refresh token'
      };
    }
  }

  /**
   * Get current access token, refresh if needed
   */
  public async getValidAccessToken(): Promise<string | null> {
    if (this.isTokenExpiredOrExpiringSoon()) {
      const refreshResult = await this.refreshAccessToken();
      if (!refreshResult.success) {
        return null;
      }
    }

    return this.getAccessToken();
  }

  /**
   * Revoke refresh token (logout)
   */
  public async revokeRefreshToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return true;

    try {
      const response = await fetch(`${API_BASE_URL}/auth/revoke`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAccessToken()}`,
        },
        body: JSON.stringify({ refreshToken }),
      });

      const result = await response.json();
      
      // Clear tokens regardless of API response
      this.clearTokens();
      
      return result.success;
    } catch (error) {
      this.clearTokens();
      return false;
    }
  }

  /**
   * Revoke all refresh tokens for current user
   */
  public async revokeAllTokens(userId: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/revoke-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAccessToken()}`,
        },
        body: JSON.stringify({ userId }),
      });

      const result = await response.json();
      
      // Clear tokens regardless of API response
      this.clearTokens();
      
      return result.success;
    } catch (error) {
      this.clearTokens();
      return false;
    }
  }
}

export const refreshTokenService = RefreshTokenService.getInstance();
